import React, {useState} from 'react';
import isEmpty from 'lodash/isEmpty';
import DownloadLink from '../DownloadLink/DownloadLink';
import {
    renderGroupTable,
    renderWeekTable,
} from '../../helper/renderScheduleTable';
import DepartmentSchedule from '../DepartmentSchedule/DepartmentSchedule';
import SchedulePublishBanner from '../GroupSchedulePage/SchedulePublishBanner/SchedulePublishBanner';
import DepartmentDownloadLink from '../DownloadLink/DepartmentDownloadLink';
import {getTeacherWithPosition} from '../../helper/renderTeacher';
import {buildMergedTeacherSchedule} from '../../helper/mergeTeacherSchedules';
import {SEMESTER_COLORS} from '../../constants/semesterColors';
import {
    checkSemesterEnd, getDayName,
    getWeekParity, isWeekOdd,
    transformSemesterDate, isShortSemester
} from "../../utils/dateUtils";
import FullScheduleTable from "../FullScheduleTable/FullScheduleTable";
import CalendarSchedule from '../CalendarSchedule/CalendarSchedule';
import CalendarGroupSchedule from '../CalendarSchedule/CalendarGroupSchedule';
import SemesterTitle from "./SemesterTitle";
import CalendarScheduleByDate from "../CalendarSchedule/CalendarScheduleByDate";
import WarningNotice from "../../share/Alerts/WarningNotice";
import CalendarDownloadLink from "../CalendarDownloadLink/CalendarDownloadLink";
import CalendarXlsxButton from '../CalendarSchedule/CalendarXlsxButton';
import i18n from "i18next";

const currentWeekIsOdd = (semester) => isWeekOdd(getWeekParity(semester.startDay));

// ─── Shared components ────────────────────────────────────────────────────────

const EmptySchedule = ({t}) => (
    <p className="empty_schedule">{t('common:empty_schedule')}</p>
);

const SemesterActions = ({label, children}) => (
    <div className="semester-actions">
        {label && <span className="semester-actions__label">{label}</span>}
        <div className="semester-actions__buttons">{children}</div>
    </div>
);

const RevisionNotice = ({revision, t}) => {
    if (!revision) return null;
    const MS_PER_DAY = 86400000;
    const HINT_VISIBLE_DAYS = 30;

    const date = new Date(revision);
    const daysAgo = (Date.now() - date) / 86400000;
    const showYear = date.getFullYear() !== new Date().getFullYear();

    const formatted = date.toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', {
        day: 'numeric',
        month: 'long',
        ...(showYear && {year: 'numeric'}),
    });

    return (
        <div className="revision-notice">
            {t('common:schedule_updated', {date: formatted})}
            {daysAgo <= HINT_VISIBLE_DAYS && (
                <span className="revision-notice__hint">
                    {t('common:reimport_hint')}
                </span>
            )}
        </div>
    );
};

export const ViewModeToggle = ({viewMode, setViewMode, t}) => (
    <div className="schedule-view-toggle">
        <button
            className={`schedule-view-toggle__btn ${viewMode === 'today' ? 'active' : ''}`}
            onClick={() => setViewMode('today')}
        >
            {t('common:today_schedule', 'Сьогодні')}
        </button>
        <button
            className={`schedule-view-toggle__btn ${viewMode === 'all' ? 'active' : ''}`}
            onClick={() => setViewMode('all')}
        >
            {t('common:full_week_schedule', 'Весь тиждень')}
        </button>
    </div>
);

const ScheduleTitleHeader = ({semester, subtitle, multiline = false, children}) => (
    <h1>
        <SemesterTitle semester={semester}/>
        {subtitle && (
            multiline ? (
                <>
                    <br/>
                    <span className="schedule-subtitle">{subtitle}</span>
                </>
            ) : (
                <>
                    <span className="schedule-title-divider"> | </span>
                    <span className="schedule-group-title">{subtitle}</span>
                </>
            )
        )}
        {children}
    </h1>
);

const ScheduleHeader = ({semester, t, toggle}) => (
    <h1>
        <span className="schedule-week-badge">
            {getWeekParity(semester.startDay)} {t('week_label')}
        </span>
        <SemesterTitle semester={semester}/>
        {toggle}
    </h1>
);

const WeekSection = ({label, isCurrentWeek, children}) => (
    <div className="week-section">
        <h2>
            <span className={isCurrentWeek ? 'currentDay' : ''}>
                {label}
            </span>
        </h2>
        {children}
    </div>
);

const TitleSuffix = ({isManager}) =>
    isManager ? (
        <div className="schedule-publish-banner-right">
            <SchedulePublishBanner/>
        </div>
    ) : null;

const SemesterEndedNotice = ({endDay, t}) =>
    checkSemesterEnd(endDay)
        ? (
            <WarningNotice className="semester-ended-notice">
                {t('common:semester_ended', 'Семестр вже завершився')}
            </WarningNotice>
        )
        : null;

// ─── Schedule views ───────────────────────────────────────────────────────────

const GroupScheduleView = ({groupSchedule, groupData, semesterData, isManager, t, viewMode, setViewMode}) => {
    const {semester, group, oddArray, evenArray, byDate} = groupSchedule;

    // A semester kept by date comes back as dates rather than as a pair of weeks, and is read as
    // it is stored. Switched on the payload rather than on anything about the semester itself,
    // because the payload is what actually differs.
    if (byDate) {
        return (
            <>
                <TitleSuffix isManager={isManager}/>
                <ScheduleTitleHeader semester={semester} subtitle={group?.title}>
                    <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} t={t}/>
                </ScheduleTitleHeader>
                <div className="schedule-actions">
                    {groupData?.id && (
                        <SemesterActions>
                            <DownloadLink entity="group" semesterId={semesterData.id} entityId={groupData.id}/>
                            <CalendarDownloadLink entity="group" semesterId={semesterData.id} entityId={groupData.id}/>
                        </SemesterActions>
                    )}
                </div>
                <SemesterEndedNotice endDay={semester.endDay} t={t}/>
                <CalendarScheduleByDate byDate={byDate} viewMode={viewMode} t={t}/>
            </>
        );
    }

    if (isEmpty(oddArray) && isEmpty(evenArray)) return <EmptySchedule t={t}/>;

    const scheduleLinks = groupData?.id && (
        <SemesterActions>
            <DownloadLink entity="group" semesterId={semesterData.id} entityId={groupData.id}/>
            <CalendarDownloadLink entity="group" semesterId={semesterData.id} entityId={groupData.id}/>
        </SemesterActions>
    );

    if (isShortSemester(semester.startDay, semester.endDay)) {
        return (
            <>
                <TitleSuffix isManager={isManager}/>
                <ScheduleTitleHeader semester={semester} subtitle={group?.title}>
                    <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} t={t}/>
                </ScheduleTitleHeader>
                <div className="schedule-actions">
                    {scheduleLinks}
                </div>
                <SemesterEndedNotice endDay={semester.endDay} t={t}/>
                <CalendarGroupSchedule groupSchedule={groupSchedule} viewMode={viewMode} t={t}/>
            </>
        );
    }


    return (
        <>
            <TitleSuffix isManager={isManager}/>
            <ScheduleTitleHeader semester={semester} subtitle={group?.title}/>
            {scheduleLinks && <div className="schedule-actions">{scheduleLinks}</div>}
            <RevisionNotice revision={groupSchedule.revision} t={t}/>
            <SemesterEndedNotice endDay={semester.endDay} t={t}/>
            <WeekSection label={t('common:odd_week')} isCurrentWeek={currentWeekIsOdd(semester)}>
                {renderGroupTable(oddArray, true, semester)}
            </WeekSection>
            <WeekSection label={t('common:even_week')} isCurrentWeek={!currentWeekIsOdd(semester)}>
                {renderGroupTable(evenArray, false, semester)}
            </WeekSection>
        </>
    );
};

export const isCardsEmpty = (cards) => Object.keys(cards).length === 0;

const TeacherScheduleView = ({teacherSchedule, isManager, t}) => {
    const schedules = (Array.isArray(teacherSchedule) ? teacherSchedule : [teacherSchedule])
        .filter(Boolean)
        .sort((a, b) =>
            transformSemesterDate(a.semester.startDay) - transformSemesterDate(b.semester.startDay)
        );

    const revision = schedules
        .map(s => s.revision)
        .filter(Boolean)
        .sort()
        .pop();

    const [selectedColor, setSelectedColor] = useState(null);

    const semesterColor = (index) => SEMESTER_COLORS[index % SEMESTER_COLORS.length];

    // A semester kept by date is read by date. The merged two-week tables below cannot hold it —
    // its items carry no parity — so it is shown on its own, in the same calendar the groups use,
    // with a column per group the teacher meets.
    const byDateSchedules = schedules.filter(({byDate}) => byDate);
    if (byDateSchedules.length > 0) {
        // Who and which semester, even when the answer is nothing: a bare "empty" leaves the
        // reader unsure whether the question was understood.
        return (
            <>
                <TitleSuffix isManager={isManager}/>
                <h1>{getTeacherWithPosition(byDateSchedules[0].teacher)}</h1>
                {byDateSchedules.map(({semester: sem, byDate}) => (
                    <div key={sem.id} className="byDate-schedule-block">
                        <ScheduleTitleHeader semester={sem} multiline/>
                        <div className="schedule-actions">
                            <SemesterActions>
                                <DownloadLink entity="teacher" semesterId={sem.id}
                                              entityId={byDateSchedules[0].teacher.id}/>
                                <CalendarDownloadLink entity="teacher" semesterId={sem.id}
                                                      entityId={byDateSchedules[0].teacher.id}/>
                            </SemesterActions>
                        </div>
                        <SemesterEndedNotice endDay={sem.endDay} t={t}/>
                        <CalendarScheduleByDate byDate={byDate} t={t}/>
                    </div>
                ))}
            </>
        );
    }

    const hasAnyClasses = schedules.some(
        ({odd, even}) => !isEmpty(odd?.classes) || !isEmpty(even?.classes)
    );
    if (isEmpty(schedules) || !hasAnyClasses) return <EmptySchedule t={t}/>;

    const teacherId = schedules[0].teacher.id;

    const {odd: mergedOdd, even: mergedEven, referenceSemester} = buildMergedTeacherSchedule(schedules);
    const handleSemesterClick = (color) => {
        setSelectedColor(prev => prev === color ? null : color);
    };

    const visibleSchedules = schedules
        .map((schedule, index) => ({schedule, color: semesterColor(index)}))
        .filter(({color}) => selectedColor === null || color === selectedColor);

    const semesterLegend = schedules.length > 1 && (
        <div className="semester-legend">
            {schedules.map(({semester: sem}, index) => {
                const color = semesterColor(index);
                const isSelected = selectedColor === color;
                const isActive = selectedColor === null || isSelected;
                return (
                    <span
                        key={sem.id}
                        className="semester-legend__item"
                        style={{
                            backgroundColor: color,
                            color: 'white',
                            opacity: isActive ? 1 : 0.4,
                            cursor: 'pointer',
                            outline: 'none',
                            boxShadow: isSelected ? '0 0 0 3px #000000aa' : 'none',
                            transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                            transition: 'transform 0.15s',
                        }}
                        onClick={() => handleSemesterClick(color)}
                    >
                        {sem.description} ({sem.startDay?.slice(0, 5)}-{sem.endDay?.slice(0, 5)})
                    </span>
                );
            })}
        </div>
    );

    const downloadLinks = visibleSchedules.map(({schedule: {semester: sem}}, index) => (
        <SemesterActions key={sem.id} label={schedules.length > 1 ? sem.description : null}>
            <DownloadLink entity="teacher" semesterId={sem.id} entityId={teacherId}/>
            <CalendarDownloadLink entity="teacher" semesterId={sem.id} entityId={teacherId}
                                  withHint={index === 0}/>
        </SemesterActions>
    ));

    return (
        <>
            <TitleSuffix isManager={isManager}/>
            {schedules.length === 1 ? (
                <ScheduleTitleHeader
                    semester={schedules[0].semester}
                    subtitle={getTeacherWithPosition(schedules[0].teacher)}
                    multiline
                />
            ) : (
                <h1>{getTeacherWithPosition(schedules[0].teacher)}</h1>
            )}
            <div className="schedule-actions">
                {downloadLinks}
                {schedules.length > 1 && (
                    <SemesterActions label={t('common:all_semesters', 'усі семестри')}>
                        <CalendarDownloadLink entity="teacherActive" entityId={teacherId} withHint={false}/>
                    </SemesterActions>
                )}
            </div>
            <RevisionNotice revision={revision} t={t}/>
            <div className="schedule-legends-row">
                {semesterLegend}
            </div>
            <SemesterEndedNotice endDay={referenceSemester.semester.endDay} t={t}/>
            <WeekSection label={t('common:odd_week')} isCurrentWeek={currentWeekIsOdd(referenceSemester.semester)}>
                {isCardsEmpty(mergedOdd.cards) ? <EmptySchedule t={t}/> : renderWeekTable(mergedOdd, selectedColor)}
            </WeekSection>
            <WeekSection label={t('common:even_week')} isCurrentWeek={!currentWeekIsOdd(referenceSemester.semester)}>
                {isCardsEmpty(mergedEven.cards) ? <EmptySchedule t={t}/> : renderWeekTable(mergedEven, selectedColor)}
            </WeekSection>
        </>
    );
};

const DepartmentScheduleView = ({fullSchedule, departmentData, isManager, t}) => {
    const {resultArray, semester} = fullSchedule;
    if (isEmpty(resultArray)) return <EmptySchedule t={t}/>;

    const subtitle = departmentData && `${t('department_label')} ${departmentData.name}`;

    return (
        <>
            <TitleSuffix isManager={isManager}/>
            <ScheduleTitleHeader semester={semester} subtitle={subtitle}>
                <DepartmentDownloadLink
                    departmentName={departmentData?.name}
                    semesterDescription={semester?.description}
                    semesterStartDay={semester?.startDay}
                    semesterEndDay={semester?.endDay}
                />
            </ScheduleTitleHeader>
            <SemesterEndedNotice endDay={semester.endDay} t={t}/>
            <DepartmentSchedule fullSchedule={fullSchedule} departmentId={departmentData?.id}/>
        </>
    );
};

const FullScheduleView = ({fullSchedule, isManager, t, viewMode, setViewMode}) => {
    const {resultArray, semester, byDate} = fullSchedule;

    // Same reasoning as the group view: a semester kept by date is read as it is stored, and is
    // recognised by the payload rather than guessed at.
    if (byDate) {
        return (
            <>
                <TitleSuffix isManager={isManager}/>
                <ScheduleHeader
                    semester={semester}
                    t={t}
                    toggle={<ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} t={t}/>}
                />
                <div className="schedule-actions">
                    <SemesterActions>
                        <CalendarXlsxButton byDate={byDate} viewMode={viewMode} t={t}/>
                    </SemesterActions>
                </div>
                <SemesterEndedNotice endDay={semester.endDay} t={t}/>
                <CalendarScheduleByDate byDate={byDate} viewMode={viewMode} t={t}/>
            </>
        );
    }

    if (isEmpty(resultArray)) return <EmptySchedule t={t}/>;

    const toggle = <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} t={t}/>;

    if (isShortSemester(semester.startDay, semester.endDay)) {
        return (
            <>
                <TitleSuffix isManager={isManager}/>
                <ScheduleHeader semester={semester} t={t} toggle={toggle}/>
                <div className="schedule-actions">
                    <SemesterActions>
                        <CalendarXlsxButton byDate={byDate} viewMode={viewMode} t={t}/>
                    </SemesterActions>
                </div>
                <SemesterEndedNotice endDay={semester.endDay} t={t}/>
                <CalendarSchedule fullSchedule={fullSchedule} viewMode={viewMode} t={t}/>
            </>
        );
    }

    const currentDay = getDayName(new Date());
    const displaySchedule = viewMode === 'today'
        ? {...fullSchedule, resultArray: resultArray.filter(d => d.day === currentDay)}
        : fullSchedule;

    const hasClassesToday = currentDay && displaySchedule.resultArray.length > 0;

    return (
        <>
            <TitleSuffix isManager={isManager}/>
            <ScheduleHeader semester={semester} t={t} toggle={toggle}/>
            <SemesterEndedNotice endDay={semester.endDay} t={t}/>
            {viewMode === 'today' && !hasClassesToday
                ? <p className="empty_schedule">{t('common:no_classes_today', 'Сьогодні немає занять')}</p>
                :
                <FullScheduleTable
                    fullResultSchedule={displaySchedule}
                    todayWeekIsOdd={viewMode === 'today' ? currentWeekIsOdd(semester) : null}
                />
            }
        </>
    );
};

// ─── Dispatcher ───────────────────────────────────────────────────────────────

const scheduleViews = {
    group: GroupScheduleView,
    teacher: TeacherScheduleView,
    department: DepartmentScheduleView,
    full: FullScheduleView,
};

const ScheduleView = (props) => {
    const {scheduleType, notPublished, notPublishedMessage, t} = props;

    if (notPublished) {
        return (
            <div className="schedule-not-published">
                <h2>{notPublishedMessage || t('common:schedule_not_published')}</h2>
            </div>
        );
    }

    const Component = scheduleViews[scheduleType];
    return Component ? <Component {...props} /> : null;
};

export {ScheduleView};
